import React, { useState, useEffect } from 'react'
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined, MailOutlined, PhoneOutlined, ProjectOutlined, IdcardOutlined, CloseOutlined } from '@ant-design/icons'
import { Tooltip, Tabs, Progress, Input, message, Modal, Spin, Skeleton } from 'antd'
import Button from 'components/Button'
import styled from 'styled-components'
import { Pie } from '@ant-design/plots'
import candidateRequest from 'utils/candidateRequest'



const Container = styled.div`
  padding: 0;
  position: relative;
`
const BasicInfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`

const AvatarCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #e8eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  color: #4169e1;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.15);
  border: 2px solid #d0d9f5;
`

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const ProfileName = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
`

const ProfileId = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
  letter-spacing: 0.3px;
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 16px;
  }
  
  .ant-tabs-tab {
    font-weight: 600;
    font-size: 13px;
  }
`

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`

const DataItem = styled.div`
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
 
  ${props => props.isInvalid && `
    border: 1px solid #ef4444;
    background: #fff1f2;
  `}
`

const DataLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const DataValue = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  word-break: break-word;
`

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  transition: all 0.2s;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.bgColor || 'transparent'};

  &:hover {
    transform: scale(1.1);
  }
`

const NoData = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`

const PieChartWrapper = styled.div`
  background: white;
  padding: 30px 40px 90px 40px;
  height: 350px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
`

const CandidateDetailsView = ({ candidateInfo, assessmentScores, normalizedData, cvData, normalizedColumns, rawData, cvValidationLog, normalizationLogs, setIsSaving, refreshCandidate,
  onPushSuccess, onPushFailed, isLoading }) => {
  const [activeTab, setActiveTab] = useState('normalized')
  useEffect(() => {
    setActiveTab('normalized')
  }, [])
  const infoLogs = normalizationLogs.filter(l => l.log_level === "INFO")
  const warningLogs = normalizationLogs.filter(l => l.log_level === "WARNING")

  const renderNormalizationSummary = () => {
    const fields = transformNormalizedData(normalizedData)

    if (!fields || fields.length === 0) {
      return <NoData>No normalized fields available</NoData>
    }

    const successCount = fields.filter(f => f.is_valid !== false && (f.value !== null && f.value !== undefined && f.value !== '')).length
    const failedCount = fields.filter(f => f.is_valid === false).length
    const emptyCount = fields.filter(f => f.is_valid !== false && (f.value === null || f.value === undefined || f.value === '')).length

    const statusConfig = {
      success: { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0', label: 'SUCCESS', icon: '✓' },
      failed: { bg: '#fee2e2', color: '#dc2626', border: '#fecaca', label: 'FAILED', icon: '✗' },
      empty: { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0', label: 'EMPTY', icon: '—' },
    }

    const getFieldStatus = (field) => {
      if (field.is_valid === false) return 'failed'
      if (field.value === null || field.value === undefined || field.value === '') return 'empty'
      return 'success'
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
          {[
            { label: 'Success', count: successCount, ...statusConfig.success },
            { label: 'Failed', count: failedCount, ...statusConfig.failed },
            { label: 'Empty', count: emptyCount, ...statusConfig.empty },
          ].map(({ label, count, bg, color, border }) => (
            <div key={label} style={{
              padding: '6px 14px', borderRadius: 6, background: bg,
              border: `1px solid ${border}`, color, fontWeight: 700, fontSize: 12
            }}>
              {count} {label}
            </div>
          ))}
        </div>

        {fields.map((field, idx) => {
          const status = getFieldStatus(field)
          const cfg = statusConfig[status]

          return (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: 8, background: '#ffffff',
              border: '1px solid #e5e7eb', borderLeft: `4px solid ${cfg.color}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: cfg.bg, border: `1px solid ${cfg.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13, color: cfg.color, flexShrink: 0
                }}>
                  {cfg.icon}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                    {formatLabel(field.key)}
                  </div>
                  {field.message && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {field.message}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <div style={{
                  fontSize: 12, color: '#374151', maxWidth: 200,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right'
                }}>
                  {field.value !== null && field.value !== undefined && field.value !== '' ? String(field.value) : '—'}
                </div>
                <div style={{
                  padding: '3px 10px', borderRadius: 4, fontWeight: 700, fontSize: 11,
                  background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  minWidth: 64, textAlign: 'center'
                }}>
                  {cfg.label}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderLogs = (logs, type) => {
    if (!logs || !logs.length) {
      return <NoData>No {type} logs</NoData>
    }

    const formatStepName = (name) => {
      if (!name) return ''
      return name.replace(/[_-]/g, ' ').toUpperCase()
    }

    const renderValue = (value) => {
      if (value === null || value === undefined) return '-'

      if (typeof value === 'object') {
        return (
          <pre style={{
            fontSize: 10,
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 6,
            borderRadius: 4,
            maxHeight: 120,
            overflow: 'auto'
          }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        )
      }

      return String(value)
    }

    const getConfidenceBadge = (scoreValue) => {
      const score = scoreValue ?? 0

      return (
        <div
          style={{
            fontSize: 12,
            padding: '2px 8px',
            borderRadius: 4,
            background: '#dcfce7',
            color: '#16a34a',
            fontWeight: 700,
            minWidth: 90,
            textAlign: 'center'
          }}
        >
          {`Confidence Score: ${score}`}
        </div>
      )
    }

    // detect AI log
    const isAIProcessingLog = (log) => {
      return (
        log.step_name?.toLowerCase() === 'ai processing' &&
        log.message?.includes('Normalization completed')
      )
    }

    // parse normalization message
    const parseNormalizationMessage = (message) => {
      try {
        // ✅ Fixed (custom UI)
        const Normalization_completed =
          message.match(/:\s*([^,]*fields in output)/)?.[1]

        const matched_slugs =
          message.match(/,\s*([^,]*matched known slugs[^,]*)/)?.[1]

        // ✅ Extract ALL key=value pairs dynamically
        const keyValuePairs = [...message.matchAll(/(\w+)=([^,]+)/g)]

        const dynamicFields = {}

        keyValuePairs.forEach(([_, key, value]) => {
          if (key === 'missing_slugs') return // handled separately

          dynamicFields[key] = value.trim()
        })

        // ✅ Missing slugs (array)
        const missingMatch = message.match(/missing_slugs=\[(.*?)\]/s)
        const missing_slugs = missingMatch
          ? missingMatch[1]
            .split(',')
            .map(s => s.trim().replace(/['"]/g, ''))
            .filter(Boolean)
          : []

        return {
          Normalization_completed,
          matched_slugs,
          dynamicFields,
          missing_slugs
        }
      } catch {
        return null
      }
    }

    const renderAIProcessingLog = (log) => {
      const parsed = parseNormalizationMessage(log.message)

      if (!parsed) return <div>{log.message}</div>

      // remove underscore for display
      const formatKey = (key) => key.replace(/_/g, ' ')

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* 4 GRID ITEMS */}
          <DataGrid>

            {/* ✅ First 2 FIXED */}
            {[
              ['Normalization completed', parsed.Normalization_completed],
              ['matched slugs', parsed.matched_slugs]
            ].map(([key, value]) => (
              <DataItem key={key}>
                <DataLabel>
                  <span>{key}</span>
                </DataLabel>
                <DataValue>{value || '-'}</DataValue>
              </DataItem>
            ))}

            {/* ✅ Dynamic fields (DB driven) */}
            {Object.entries(parsed.dynamicFields).map(([key, value]) => (
              <DataItem key={key}>
                <DataLabel>
                  <span>{key.replace(/_/g, ' ')}</span>
                </DataLabel>
                <DataValue>{value || '-'}</DataValue>
              </DataItem>
            ))}

          </DataGrid>
          {/* missing slugs */}
          {parsed.missing_slugs.length > 0 && (
            <div style={{ width: '100%' }}>
              <DataLabel style={{ marginBottom: 6 }}>
                <span>missing slugs</span>
              </DataLabel>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  maxHeight: 120,
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  padding: 8,
                  background: '#f9fafb'
                }}
              >
                {parsed.missing_slugs.map((slug, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: 11,
                      padding: '3px 6px',
                      borderRadius: 4,
                      background: '#fee2e2',
                      color: '#b91c1c',
                      border: '1px solid #fecaca'
                    }}
                  >
                    {slug}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }

    // INFO first → WARNING next
    const sortedLogs = [...logs].sort((a, b) => {
      if (a.log_level === 'INFO' && b.log_level === 'WARNING') return -1
      if (a.log_level === 'WARNING' && b.log_level === 'INFO') return 1
      return new Date(b.created_at) - new Date(a.created_at)
    })

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sortedLogs.map((log, index) => (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',   // ✅ stack vertically
              gap: 8,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              borderLeft: `4px solid ${log.log_level === 'WARNING' ? '#f59e0b' : '#3249D7'}`
            }}
          >
            {/* TOP ROW → step + score + date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#111827',
                    letterSpacing: 0.5
                  }}
                >
                  {formatStepName(log.step_name)}
                </div>

                {getConfidenceBadge(log.confidence_score)}
              </div>

              {/* ✅ DATE moved here */}
              <div
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  whiteSpace: 'nowrap'
                }}
              >
                {log.created_at
                  ? new Date(log.created_at).toLocaleString()
                  : '-'}
              </div>
            </div>

            {/* BOTTOM → full width message */}
            <div
              style={{
                fontSize: 12,
                color: '#4b5563',
                lineHeight: 1.5
              }}
            >
              {isAIProcessingLog(log)
                ? renderAIProcessingLog(log)
                : log.message || '-'}
            </div>

            {/* DETAILS → fuzzy match info */}
            {log.details && Object.keys(log.details).length > 0 && (
              <div style={{
                marginTop: 6,
                padding: '8px 10px',
                borderRadius: 6,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Match Details
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                  {Object.entries(log.details).map(([key, value]) => {
                    if (value === null || value === undefined) return null
                    // skip internal LLM fields not meant for display
                    if (['prompt', 'raw_response', 'input_tokens', 'output_tokens'].includes(key)) return null
                    const isStatus = key === 'status'
                    const isSuccess = value === 'success'
                    const isFailed = value === 'failed'
                    const isSkipped = value === 'skipped'
                    const isFuzzyList = key === 'fuzzy_matched_list' && Array.isArray(value)
                    const isLlmResponse = key === 'llm_response'
                    const isArray = Array.isArray(value)

                    if (isFuzzyList) {
                      return (
                        <div key={key} style={{ width: '100%', marginTop: 4 }}>
                          <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>fuzzy matched list:</span>
                          {value.length === 0 ? (
                            <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>No fuzzy matches found</div>
                          ) : (
                            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {value.map((item, i) => (
                                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 10, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4 }}>
                                  <span style={{ color: '#64748b', minWidth: 32 }}>#{i + 1}</span>
                                  <span style={{ color: '#0f172a', fontWeight: 600, flex: 1 }}>{item.name}</span>
                                  <span style={{ color: '#6366f1' }}>score: {item.score}</span>
                                  <span style={{ color: '#94a3b8' }}>id: {item.id}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (isLlmResponse) {
                      let parsed = null
                      try { parsed = JSON.parse(value) } catch (_) { }
                      const isZeroFuzzy = !parsed && (log.details?.fuzzy_score === 0 || log.details?.fuzzy_score === '0')
                      return (
                        <div key={key} style={{ width: '100%', marginTop: 4 }}>
                          <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>llm response:</span>
                          {parsed ? (
                            <pre style={{
                              marginTop: 4, padding: '6px 8px', borderRadius: 4,
                              background: '#0f172a', color: '#e2e8f0', fontSize: 10,
                              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              maxHeight: 120, overflowY: 'auto',
                            }}>
                              {JSON.stringify(parsed, null, 2)}
                            </pre>
                          ) : (
                            <span style={{ marginLeft: 6, color: '#374151', fontSize: 11 }}>
                              {renderValue(value)}
                              {isZeroFuzzy && (
                                <span style={{ marginLeft: 6, color: '#f59e0b', fontStyle: 'italic', fontSize: 10 }}>
                                  (LLM expanded — 0 fuzzy matches)
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      )
                    }

                    const isComplexValue = (isArray && value.length > 0 && !value.every(item => typeof item !== 'object' || item === null))
                      || (typeof value === 'object' && value !== null && !isArray)

                    return (
                      <div key={key} style={{
                        display: 'flex',
                        flexDirection: isComplexValue ? 'column' : 'row',
                        alignItems: 'flex-start',
                        gap: 4,
                        fontSize: 11,
                        width: isComplexValue ? '100%' : undefined,
                      }}>
                        <span style={{ color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {key.replace(/_/g, ' ')}:
                        </span>
                        {isStatus ? (
                          <span style={{
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontWeight: 700,
                            fontSize: 10,
                            background: isSuccess ? '#dcfce7' : isFailed ? '#fee2e2' : '#f1f5f9',
                            color: isSuccess ? '#16a34a' : isFailed ? '#dc2626' : '#64748b',
                            border: `1px solid ${isSuccess ? '#bbf7d0' : isFailed ? '#fecaca' : '#e2e8f0'}`
                          }}>
                            {String(value).toUpperCase()}
                          </span>
                        ) : isArray ? (
                          value.length === 0 ? (
                            <span style={{ color: '#374151' }}>-</span>
                          ) : value.every(item => typeof item !== 'object' || item === null) ? (
                            <span style={{ color: '#374151' }}>{value.join(', ')}</span>
                          ) : (
                            <pre style={{
                              margin: 0, padding: '4px 6px', borderRadius: 4,
                              background: '#0f172a', color: '#e2e8f0', fontSize: 10,
                              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              maxHeight: 150, overflowY: 'auto', width: '100%', boxSizing: 'border-box',
                            }}>
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          )
                        ) : typeof value === 'object' && value !== null ? (
                          <pre style={{
                            margin: 0, padding: '4px 6px', borderRadius: 4,
                            background: '#0f172a', color: '#e2e8f0', fontSize: 10,
                            overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            maxHeight: 150, overflowY: 'auto', width: '100%', boxSizing: 'border-box',
                          }}>
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : (
                          <span style={{ color: '#374151', wordBreak: 'break-all' }}>{String(value)}</span>
                        )}
                      </div>
                    )
                  })}
                  {/* Fallback: show fuzzy_matched_list section for matching steps even when key is absent */}
                  {log.step_name?.includes('matching') && !log.details?.fuzzy_matched_list && (
                    <div style={{ width: '100%', marginTop: 4 }}>
                      <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>fuzzy matched list:</span>
                      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>No fuzzy matches found</div>
                    </div>
                  )}
                  {/* Fallback: show llm response section for matching steps even when key is absent */}
                  {log.step_name?.includes('matching') && !log.details?.llm_response && (
                    <div style={{ width: '100%', marginTop: 4 }}>
                      <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>llm response:</span>
                      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
                        {log.details?.fuzzy_score >= 90
                          ? 'LLM not called — high confidence fuzzy match'
                          : 'No LLM response available'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* date */}

          </div>
        ))}
      </div>
    )
  }
  const formatLabel = (key) => {
    if (!key) return ''
    return normalizedColumns?.[key] || key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getValidationIcon = (isValid, message) => {
    if (isValid === true) {
      return (
        <Tooltip title={message || 'Valid'} placement="top">
          <ValidationIcon bgColor="#d1fae5">
            <CheckCircleOutlined style={{ fontSize: 14, color: '#10b981' }} />
          </ValidationIcon>
        </Tooltip>
      )
    } else if (isValid === false) {
      return (
        <Tooltip title={message || 'Invalid'} placement="top">
          <ValidationIcon bgColor="#fee2e2">
            <CloseCircleOutlined style={{ fontSize: 12, color: '#ef4444' }} />
          </ValidationIcon>
        </Tooltip>
      )
    }
    return null
  }

  const transformNormalizedData = (data) => {
    if (!data) return []
    if (Array.isArray(data)) return data
    return Object.entries(data).map(([key, value]) => ({
      key,
      value,
      is_valid: true,
      message: ''
    }))
  }

  const renderCVData = (data) => {
    const renderValue = (value, key) => {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      // DOB stored as ISO (YYYY-MM-DD) — display as DD/MM/YYYY
      if ((key === 'dob' || key === 'date_of_birth') && typeof value === 'string') {
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (m) return `${m[3]}/${m[2]}/${m[1]}`
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return '-'

        // Check if array contains objects
        if (value.some(item => typeof item === 'object' && item !== null)) {
          // For simple objects with just name/id (like skills)
          const isSimpleObjectArray = value.every(item =>
            typeof item === 'object' &&
            item !== null &&
            Object.keys(item).length <= 2 &&
            (item.name || item.id)
          )

          if (isSimpleObjectArray) {
            const names = value.map(item => item.name || item.id || '').filter(Boolean)
            return names.length > 0 ? names.join(', ') : '-'
          }

          // For complex objects (like projects, education)
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {value.map((item, idx) => (
                <div key={idx} style={{
                  padding: 8,
                  background: '#fff',
                  borderRadius: 4,
                  border: '1px solid #e5e7eb'
                }}>
                  {typeof item === 'object' && item !== null ? (
                    Object.entries(item)
                      .filter(([k, v]) => {
                        if (v === null || v === undefined || v === '') return false
                        if (Array.isArray(v) && v.length === 0) return false
                        if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0) return false
                        return true
                      })
                      .map(([k, v]) => (
                        <div key={k} style={{ fontSize: 11, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280' }}>{formatLabel(k)}:</span>{' '}
                          <span style={{ color: '#111827' }}>
                            {Array.isArray(v)
                              ? v.map(subItem =>
                                typeof subItem === 'object' && subItem !== null
                                  ? (subItem.name || subItem.id || JSON.stringify(subItem))
                                  : String(subItem)
                              ).filter(Boolean).join(', ')
                              : typeof v === 'object' && v !== null
                                ? Object.entries(v)
                                  .filter(([sk, sv]) => sv !== null && sv !== undefined && sv !== '')
                                  .map(([sk, sv]) => `${formatLabel(sk)}: ${sv}`)
                                  .join(', ') || '-'
                                : String(v)
                            }
                          </span>
                        </div>
                      ))
                  ) : (
                    String(item)
                  )}
                </div>
              ))}
            </div>
          )
        }

        return value.filter(v => v !== null && v !== undefined && v !== '').join(', ') || '-'
      }

      if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([k, v]) => {
          if (v === null || v === undefined || v === '') return false
          if (Array.isArray(v) && v.length === 0) return false
          if (typeof v === 'object' && v !== null && Object.keys(v).length === 0) return false
          return true
        })

        if (entries.length === 0) return '-'

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {entries.map(([k, v]) => (
              <div key={k} style={{ fontSize: 11 }}>
                <span style={{ fontWeight: 600, color: '#6b7280' }}>{formatLabel(k)}:</span>{' '}
                <span style={{ color: '#111827' }}>
                  {Array.isArray(v)
                    ? v.map(item =>
                      typeof item === 'object' && item !== null
                        ? (item.name || item.id || JSON.stringify(item))
                        : String(item)
                    ).filter(Boolean).join(', ')
                    : typeof v === 'object' && v !== null
                      ? Object.entries(v)
                        .filter(([sk, sv]) => sv !== null && sv !== undefined && sv !== '')
                        .map(([sk, sv]) => `${formatLabel(sk)}: ${sv}`)
                        .join(', ') || '-'
                      : String(v)
                  }
                </span>
              </div>
            ))}
          </div>
        )
      }

      return String(value)
    }

    const filteredData = (!data || typeof data !== 'object') ? [] : Object.entries(data).filter(([key, value]) => {
      if (value === null || value === undefined || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0) return false
      return true
    })

    if (filteredData.length === 0) {
      return (
        <NoData>
          {cvValidationLog?.log_type === 'cv_parse_error' || cvValidationLog?.log_type === 'cv_parse' 
            ? <>
              <div style={{ marginBottom: 6, fontWeight: 600, color: '#ef4444' }}>CV Error</div>
              <div style={{ color: '#6b7280', fontSize: 12 }}>{cvValidationLog.remark}</div>
            </>
            : 'No CV data available'
          }
        </NoData>
      )
    }

    return (
      <DataGrid>
        {filteredData.map(([key, value]) => (
          <DataItem key={key}>
            <DataLabel>{formatLabel(key)}</DataLabel>
            <DataValue>{renderValue(value, key)}</DataValue>
          </DataItem>
        ))}
      </DataGrid>
    )
  }

  const renderAssessmentScores = () => {
    const aptitude = assessmentScores?.aptitude_score || 0
    const behavior = assessmentScores?.behavior_score || 0
    const communication = assessmentScores?.communication_score || 0

    if (!aptitude && !behavior && !communication) {
      return <NoData>No assessment scores available</NoData>
    }

    const pieData = [
      { type: 'Aptitude', value: parseFloat(aptitude) || 0 },
      { type: 'Behavior', value: parseFloat(behavior) || 0 },
      { type: 'Communication', value: parseFloat(communication) || 0 }
    ].filter(item => item.value > 0)

    const config = {
      data: pieData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.9,
      innerRadius: 0.65,
      label: {
        text: (d) => `${d.type}\n${d.value.toFixed(1)}`,
        position: 'spider',
        style: {
          fontSize: 12,
          fontWeight: '600',
          fill: '#6b7280'
        },
        connector: {
          style: {
            stroke: '#d1d5db',
            lineWidth: 1
          }
        }
      },
      autoFit: false,
      legend: false,
      tooltip: {
        title: 'type',
        items: [
          {
            field: 'value',
            name: 'Score',
            valueFormatter: (v) => v.toFixed(2)
          }
        ]
      },
      annotations: [
        {
          type: 'text',
          style: {
            text: 'Assessment\nScores',
            x: '50%',
            y: '50%',
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 'bold',
            fill: '#1f2937'
          }
        }
      ],
      scale: {
        color: {
          range: ['#667eea', '#764ba2', '#f59e0b']
        }
      },
      style: {
        stroke: '#fff',
        lineWidth: 2
      }
    }

    return (
      <ChartContainer>
        <PieChartWrapper>
          <Pie {...config} height={280} />
        </PieChartWrapper>
      </ChartContainer>
    )
  }

  //const normalizedArray = transformNormalizedData(normalizedData)


  const renderRawData = () => {
    if (!rawData || typeof rawData !== 'object' || Object.keys(rawData).length === 0) {
      return <NoData>No raw data available</NoData>
    }

    const formatRawValue = (value) => {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    }

    return (
      <DataGrid>
        {Object.entries(rawData).map(([key, value]) => (
          <DataItem key={key}>
            <DataLabel>
              <span>{key}</span>
            </DataLabel>
            <DataValue>{formatRawValue(value)}</DataValue>
          </DataItem>
        ))}
      </DataGrid>
    )
  }

  const [errors, setErrors] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState({})
  const isValidDate = (dateString) => {
    const date = new Date(dateString)

    // Check valid date object
    if (isNaN(date.getTime())) return false

    // Ensure exact match (prevents 2000-04-234 issue)
    const [y, m, d] = dateString.split('-').map(Number)

    return (
      date.getFullYear() === y &&
      date.getMonth() + 1 === m &&
      date.getDate() === d
    )
  }
  const validateFields = () => {
    const newErrors = {}

    normalizedArray.forEach(item => {

      if (item.is_valid !== false) return  // ✅ only invalid fields

      const key = item.key
      const value = editedData[key]

      const cleanValue =
        typeof value === 'string' ? value.trim() : value

      if (!cleanValue) return

      if (key === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(cleanValue)) {
          newErrors[key] = 'Invalid email format'
        }
      }

      if (key === 'mobile' || key === 'phone') {
        const phoneRegex = /^[0-9]{10}$/
        if (!phoneRegex.test(cleanValue)) {
          newErrors[key] = 'Mobile must be 10 digits'
        }
      }

      if (key === 'dob' || key === 'date_of_birth') {
        if (cleanValue && !isValidDate(cleanValue)) {
          newErrors[key] = 'Invalid DOB (use YYYY-MM-DD)'
        }
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  useEffect(() => {
    // Don't overwrite edited data if user is currently editing
    if (isEditing) return

    if (normalizedArray.length > 0) {
      const obj = {}
      normalizedArray.forEach(item => {
        obj[item.key] = item.value
      })
      setEditedData(obj)
    }
  }, [normalizedData, isEditing])

  const handleChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const [clearLoading, setClearLoading] = useState(false)
  const [pushLoading, setPushLoading] = useState(false)

  const [refreshKey, setRefreshKey] = useState(0)

  const formatPayload = (data) => {
    const numericFields = [
      'experience',
      'total_experience',
      'current_ctc_fixed',
      'current_ctc_variable',
      'pincode',
      'pg_year_of_passing',
      'ug_year_of_passing'
    ]

    const booleanFields = [
      'have_minimun_2_years_of_rm_experience'
    ]

    const formatted = {}

    Object.entries(data).forEach(([key, value]) => {
      if (numericFields.includes(key)) {
        formatted[key] = value ? Number(value) : null
      }
      else if (booleanFields.includes(key)) {
        formatted[key] = value === 'True' || value === true
      }
      else {
        formatted[key] = value
      }
    })

    return formatted
  }

  const convertToArrayFormat = (data) => {
    return Object.entries(data).map(([key, value]) => ({
      key: String(key),
      value: value !== null && value !== undefined ? String(value) : "",

    }))
  }

  const [localNormalizedData, setLocalNormalizedData] = useState([])
  const normalizedArray = localNormalizedData

  useEffect(() => {
    // prevent overwrite while editing OR immediately after save
    // if (isEditing || clearLoading) return
    if (isEditing || clearLoading) return

    const transformed = transformNormalizedData(normalizedData)

    // ONLY update if data actually changed AND we're not in the middle of saving
    const isSame =
      JSON.stringify(transformed) === JSON.stringify(localNormalizedData)

    if (isSame) return

    // Additional check: don't overwrite if we just saved data
    const hasUnsavedChanges = Object.keys(editedData).some(key =>
      localNormalizedData.find(item => item.key === key)?.value !== editedData[key]
    )

    if (hasUnsavedChanges && !isEditing) return

    setLocalNormalizedData(transformed)

    const obj = {}
    transformed.forEach(item => {
      obj[item.key] = item.value
    })
    setEditedData(obj)

  }, [normalizedData, isEditing, clearLoading, editedData])

  // useEffect(() => {
  //   if (isEditing) return

  //   const transformed = transformNormalizedData(normalizedData)
  //   setLocalNormalizedData(transformed)

  //   const obj = {}
  //   transformed.forEach(item => {
  //     obj[item.key] = item.value
  //   })
  //   setEditedData(obj)

  // }, [normalizedData])

  const handlePushToApi = async () => {
    const extractErrorMessage = (err) => {
      try {
        const detail = err?.detail

        // ✅ CASE 1: detail is stringified JSON
        if (typeof detail === 'string') {
          try {
            const parsed = JSON.parse(detail)
            if (parsed?.message) return parsed.message
          } catch {
            return detail // plain string fallback
          }
        }

        // ✅ CASE 2: detail is object
        if (typeof detail === 'object' && detail !== null) {
          if (detail.message) return detail.message

          // FastAPI validation array
          if (Array.isArray(detail)) {
            return detail.map(e => e.msg).join(', ')
          }
        }

        // ✅ CASE 3: normal message
        if (err?.message) return err.message

        return 'Something went wrong'
      } catch {
        return 'Something went wrong'
      }
    }
    try {
      if (!candidateInfo?.id) {
        message.error("Candidate ID missing")
        return
      }

      setPushLoading(true)
      setIsSaving?.(true)

      const payload = {
        candidate_id: candidateInfo?.id
      }

      const res = await candidateRequest.post(
        '/api/candidates/sync-completed-candidates',
        payload
      )

      const apiResponse = res?.data

      if (apiResponse?.status) {
        message.success('Candidate pushed successfully')
        // await refreshCandidate?.()
        await new Promise(resolve => setTimeout(resolve, 100))
        onPushSuccess?.()
      } else {
        const errorMsg = extractErrorMessage(apiResponse)
        message.error(errorMsg)
        onPushFailed?.()
      }

    } catch (err) {
      console.error("PUSH ERROR:", err)
      const errorMsg = extractErrorMessage(err?.response?.data || err)
      message.error(errorMsg)
      onPushFailed?.()
      // } finally {
      //   setPushLoading(false)
      //   setIsSaving?.(false)
      //   await refreshCandidate?.() 
      // }
    } finally {
      setPushLoading(false)
      setIsSaving?.(false)
      await refreshCandidate?.()
      try {
        const freshRes = await candidateRequest.get(`/api/candidates/${candidateInfo?.id}`)
        const fresh = freshRes?.data?.data
        if (fresh?.normalized_data) {
          const freshArray = fresh.normalized_data.filter(item => item.key !== 'highest_qualification')
          setLocalNormalizedData(freshArray)
          setEditedData(Object.fromEntries(freshArray.map(i => [i.key, i.value])))
        }
      } catch (_) { }
    }
  }


  const handleSave = async () => {
    if (clearLoading) return;

    if (!validateFields()) {
      message.error('Please fix validation errors')
      return
    }

    const extractErrorMessage = (err) => {
      try {
        // ✅ FastAPI error (string)
        if (typeof err?.response?.data?.detail === 'string') {
          return err.response.data.detail
        }

        // ✅ FastAPI error (object)
        if (err?.response?.data?.detail?.message) {
          return err.response.data.detail.message
        }

        // ✅ Normal backend message
        if (err?.response?.data?.message) {
          if (Array.isArray(err.response.data.message)) {
            return err.response.data.message.join(', ')
          }
          return err.response.data.message
        }

        if (err?.message) return err.message

        return 'Something went wrong. Please try again.'
      } catch {
        return 'Something went wrong. Please try again.'
      }
    }

    try {
      if (!candidateInfo?.id) {
        message.error("Candidate ID missing")
        return
      }

      setClearLoading(true)
      setIsSaving?.(true)

      const payload = {
        candidate_id: candidateInfo?.id,
        normalized_data: convertToArrayFormat(
          formatPayload(editedData)
        )
      }

      const res = await candidateRequest.post(
        '/api/candidates/normalized/update',
        payload
      )

      const apiResponse = res?.data

      // if (apiResponse?.status) {

      //   message.success('Normalized data updated successfully')

      //   // const updatedArray = localNormalizedData.map(item => ({
      //   //   ...item,
      //   //   value: editedData[item.key] ?? item.value,
      //   // }))

      //   // const updatedArray = localNormalizedData.map(item => ({
      //   //   ...item,
      //   //   value: editedData[item.key] ?? item.value,
      //   //   is_valid: item.is_valid === false
      //   //     ? (editedData[item.key] !== item.value && editedData[item.key] !== '' && editedData[item.key] !== null)
      //   //       ? true        // value was changed → mark valid
      //   //       : false       // value unchanged → keep invalid
      //   //     : item.is_valid // was already valid → leave it
      //   // }))

      //   const wasFixed = (item) =>
      //     item.is_valid === false &&
      //     editedData[item.key] !== item.value &&
      //     editedData[item.key] !== '' &&
      //     editedData[item.key] !== null

      //   const updatedArray = localNormalizedData.map(item => ({
      //     ...item,
      //     value: editedData[item.key] ?? item.value,      // ← updated value immediately
      //     is_valid: wasFixed(item) ? true : item.is_valid,
      //     message: wasFixed(item) ? 'Valid' : item.message // ← tooltip updated immediately
      //   }))

      //   // ✅ update UI
      //   setLocalNormalizedData(updatedArray)

      //   // ✅ FIX: sync editedData (THIS WAS MISSING 🔥)
      //   setEditedData(
      //     Object.fromEntries(
      //       updatedArray.map(i => [i.key, i.value])
      //     )
      //   )

      //   setIsSaved(true)
      //   setIsEditing(false)
      //   await refreshCandidate?.()
      //   // onSaveSuccess?.()
      // if (apiResponse?.status) {
      //   message.success('Normalized data updated successfully')
      //   setIsEditing(false)
      //   try {
      //     const freshRes = await candidateRequest.get(`/api/candidates/${candidateInfo?.id}`)
      //     const fresh = freshRes?.data?.data
      //     if (fresh?.normalized_data) {
      //       const freshArray = fresh.normalized_data.filter(
      //         item => item.key !== 'highest_qualification'
      //       )
      //       setLocalNormalizedData(freshArray)
      //       setEditedData(Object.fromEntries(freshArray.map(i => [i.key, i.value])))
      //       // setIsSaved(false)
      //     }
      //   } catch (e) {
      //     // fallback: keep existing local state
      //   }
      //   // setIsSaved(true)
      //   await refreshCandidate?.()

      if (apiResponse?.status) {
        message.success('Normalized data updated successfully')
        setIsEditing(false)
        try {
          const freshRes = await candidateRequest.get(`/api/candidates/${candidateInfo?.id}`)
          const fresh = freshRes?.data?.data
          if (fresh?.normalized_data) {
            const freshArray = fresh.normalized_data.filter(item => item.key !== 'highest_qualification')
            setLocalNormalizedData(freshArray)
            setEditedData(Object.fromEntries(freshArray.map(i => [i.key, i.value])))
          }
        } catch (_) { }
      } else {
        const errorMsg = extractErrorMessage(apiResponse)

        message.error(errorMsg)

        setIsEditing(false)
        // onSaveFailed?.()
      }

    } catch (err) {
      console.error("UPDATE ERROR:", err?.response || err)

      const errorMsg = extractErrorMessage(err)

      message.error(errorMsg)

      setIsEditing(false)
      onSaveFailed?.()
      // } finally {
      //   setClearLoading(false)
      //   setIsSaving?.(false)
      //   // await refreshCandidate?.()
      // }
    } finally {
      setClearLoading(false)
      setIsSaving?.(false)
      try {
        const freshRes = await candidateRequest.get(`/api/candidates/${candidateInfo?.id}`)
        const fresh = freshRes?.data?.data
        if (fresh?.normalized_data) {
          const freshArray = fresh.normalized_data.filter(item => item.key !== 'highest_qualification')
          setLocalNormalizedData(freshArray)
          setEditedData(Object.fromEntries(freshArray.map(i => [i.key, i.value])))
        }
      } catch (_) { }
      refreshCandidate?.()  // fire and forget, don't await — so it doesn't race with local state
    }
  }


  const showPushConfirm = () => {
    Modal.confirm({
      title: 'Confirm Push',
      content: (
        <div>
          <div>Do you want to push this candidate to API?</div>
          <div style={{ marginTop: 8, fontWeight: 600 }}>
            This action will sync the candidate data.
          </div>
        </div>
      ),
      okText: 'Yes, Push',
      cancelText: 'Cancel',
      okType: 'primary',

      onOk: async () => {
        await handlePushToApi()
      }
    })
  }
  const allFieldsValid = normalizedArray.length > 0 &&
    normalizedArray.every(item => item.is_valid !== false)

  const invalidCount = normalizedArray.filter(item => item.is_valid === false).length
  const tabItems = [

    {
      key: 'normalized',
      label: 'Normalized Data',
      children: (
        <>
          {/* ACTION BUTTONS */}
          <div style={{ marginBottom: 12, textAlign: 'right' }}>

            <Button
              type="primary"
              onClick={showPushConfirm}
              disabled={pushLoading || !allFieldsValid}
              style={{
                marginRight: 8,
                background: allFieldsValid ? '#10b981' : '#9ca3af',
                borderColor: allFieldsValid ? '#10b981' : '#9ca3af',
                cursor: allFieldsValid ? 'pointer' : 'not-allowed'
              }}
            >
              {pushLoading ? "Sync..." : "Push to API"}
            </Button>

            {!isEditing ? (
              <Tooltip
                title={invalidCount < 1 ? "No invalid fields" : ""}
              >
                <span>
                  <Button
                    type="secondary"
                    onClick={() => setIsEditing(true)}
                    //disabled={allFieldsValid}
                  //disabled={invalidCount < 1}
                  >
                    Edit
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <>

                <Button
                  type="primary"
                  onClick={handleSave}
                  loading={clearLoading}
                  disabled={clearLoading}
                  style={{ marginRight: 8 }}
                >
                  {clearLoading ? "Validating..." : "Verify"}
                </Button>

                <Button
                  type="secondary"
                  onClick={() => setIsEditing(false)}
                  disabled={clearLoading}       // 🔥 block cancel while saving
                >
                  Cancel
                </Button>
              </>
            )}
          </div>

          {/* DATA GRID */}
          {normalizedArray.length > 0 ? (
            <DataGrid>
              {normalizedArray.map((item, index) => (
                <DataItem key={item.key} isInvalid={item.is_valid === false}>
                  <DataLabel>
                    <span>{formatLabel(item.key)}</span>
                    {getValidationIcon(item.is_valid, item.message)}
                  </DataLabel>

                  <DataValue>
                    {isEditing && item.is_valid === false ? (
                      <div>
                        <Input
                          value={editedData[item.key] || ''}
                          onChange={(e) =>
                            handleChange(item.key, e.target.value)
                          }
                          status={errors[item.key] ? 'error' : ''}
                        />

                        {/* 🔴 Error message */}
                        {errors[item.key] && (
                          <div style={{
                            color: '#ef4444',
                            fontSize: 11,
                            marginTop: 4
                          }}>
                            {errors[item.key]}
                          </div>
                        )}
                      </div>
                    ) : (
                      typeof item.value === 'object'
                        ? JSON.stringify(item.value)
                        : item.value || '-'
                    )}
                  </DataValue>
                </DataItem>
              ))}
            </DataGrid>
          ) : (
            <NoData>No normalized data available</NoData>
          )}
        </>
      )
    },
    {
      key: 'logs',
      label: 'Normalization Logs',
      children: (
        <Tabs
          defaultActiveKey="summary"
          items={[
            {
              key: 'summary',
              label: 'Summary',
              children: renderNormalizationSummary()
            },
            {
              key: 'info',
              label: `INFO (${infoLogs.length})`,
              children: renderLogs(infoLogs, "INFO")
            },
            {
              key: 'warning',
              label: `WARNING (${warningLogs.length})`,
              children: renderLogs(warningLogs, "WARNING")
            }
          ]}
        />
      )
    },

    // {
    //   key: 'assessment',
    //   label: 'Assessment Scores',
    //   children: renderAssessmentScores()
    // },
    {
      key: 'cv',
      label: 'CV Data',
      children: renderCVData(cvData)
    },
    {
      key: 'raw',
      label: 'Raw Data',
      children: renderRawData()
    },
  ]

  return (
    <Container>
      {/* <BasicInfoSection>
        <AvatarCircle>
          {candidateInfo?.name ? candidateInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'NA'}
        </AvatarCircle>
        <ProfileInfo>
          <ProfileName>{candidateInfo?.name || 'N/A'}</ProfileName>
          <ProfileId>{candidateInfo?.id || candidateInfo?.email || ''}</ProfileId>
        </ProfileInfo>
      </BasicInfoSection> */}

      <BasicInfoSection>
        <AvatarCircle>
          {isLoading
            ? <Spin size="default" />
            : (candidateInfo?.name ? candidateInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'NA')
          }
        </AvatarCircle>
        <ProfileInfo>
          {isLoading ? (
            <>
              <Skeleton.Input active size="default" style={{ width: 180, height: 22, borderRadius: 4, marginBottom: 6 }} />
              <Skeleton.Input active size="small" style={{ width: 120, height: 16, borderRadius: 4 }} />
            </>
          ) : (
            <>
              <ProfileName>{candidateInfo?.name || 'N/A'}</ProfileName>
              <ProfileId>{candidateInfo?.role || ''}</ProfileId>
              {candidateInfo?.source_file && (
                <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2, fontWeight: 500 }}>
                  📄 {candidateInfo?.source_file}
                </div>
              )}
            </>
          )}
        </ProfileInfo>
      </BasicInfoSection>

      {(candidateInfo?.api_sync_status === 'failed' || candidateInfo?.create_full_student_response) && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px 14px',
          background: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          {/* Error header */}
          <div>
            <span style={{ color: '#f5222d', fontWeight: 700, fontSize: 13, display: 'block' }}>✕ API Failed</span>
          </div>

          {/* Response payload */}
          {(candidateInfo?.api_sync_message || candidateInfo?.create_full_student_response) && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#cf1322', marginBottom: 4, textTransform: 'uppercase' }}>
                Response
              </div>
              <pre style={{
                margin: 0, fontSize: 11, background: '#1e293b', color: '#e2e8f0',
                padding: '8px 10px', borderRadius: 6,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 160, overflowY: 'auto',
              }}>
                {candidateInfo?.api_sync_message || candidateInfo?.create_full_student_response}
              </pre>
            </div>
          )}
        </div>
      )}

      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={isLoading
          ? tabItems.map(tab => ({
              ...tab,
              children: (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <Spin size="large" />
                </div>
              )
            }))
          : tabItems
        }
      />
    </Container>
  )
}

export default CandidateDetailsView
